import { useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect } from "react";
import { Container } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { SASS, SASS_TEMPLATE } from "@/models/stff";
import { useCoreData } from "@/queries.ts";
import { FiUsers } from "react-icons/fi";
import { FaBriefcase, FaChalkboardTeacher } from "react-icons/fa";

export function SASSAddEditForm({
  selectedStaffAssignment,
  form,
  sassDefaults,
}: {
  selectedStaffAssignment: SASS | undefined;
  form: UseFormReturn<SASS, any, SASS>;
  sassDefaults?: Partial<SASS>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/staff/$staffId",
  });

  const newStaffAssignmentTemplate: SASS = {
    ...SASS_TEMPLATE,
    ...(sassDefaults || {}),
  };

  const staffAssignment = selectedStaffAssignment ?? newStaffAssignmentTemplate;

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("SASSEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("SASS");

  const {
    control,
    register,
    reset,
    watch,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset(staffAssignment);
  }, [selectedStaffAssignment]);

  const programYear = watch("ProgramYear");
  const classroomRole1 = watch("ClassroomRole1");
  const classroomRole2 = watch("ClassroomRole2");
  const nonClassroomRole1 = watch("NonClassroomRole1");
  const nonClassroomRole2 = watch("NonClassroomRole2");
  const nonClassroomRole3 = watch("NonClassroomRole3");

  const validatePercentage = (
    value: number | string | undefined,
    roleValue: string | undefined
  ) => {
    if (!roleValue || roleValue === "") {
      return true;
    }

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === 0 ||
      value === "0"
    ) {
      return "Percentage is required when role is selected";
    }

    const num = typeof value === "string" ? Number(value) : value;

    if (isNaN(num)) {
      return "Must be a valid number";
    }

    if (!Number.isInteger(num)) {
      return "Must be an integer (no decimals)";
    }

    if (num < 1 || num > 200) {
      return "Must be between 1 and 200";
    }

    return true;
  };

  return (
    <Container
      className="ps-3 pe-3 personal-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiUsers className="section-icon" />
          <h5 className="section-title">Staff Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Program Year is required" })}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id, { required: "Preschool is required" })}
                  options={leaContext
                    .currentOrgPreschoolsByProgramYear(programYear)
                    .map((x) => {
                      return {
                        Text: x.PreschoolName,
                        Value: x.PreschoolCDSCode,
                      };
                    })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="LocalStaffId"
              labelText="Staff Local ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!staffAssignment.LocalStaffId}
                  maxLength={50}
                  {...register(id, { required: "Staff Local ID is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaChalkboardTeacher className="section-icon" />
          <h5 className="section-title">Classroom Roles</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="ClassroomRole1"
              labelText="Role 1"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={coreData.staffRoleList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="ClassroomRole1PercentOfTime"
              labelText="Role 1 Full-Time Equivalency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!classroomRole1}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={9}
                  {...register(id, {
                    validate: (value) =>
                      validatePercentage(value, classroomRole1),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="ClassroomRole2"
              labelText="Role 2"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={coreData.staffRoleList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="ClassroomRole2PercentOfTime"
              labelText="Role 2 Full-Time Equivalency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!classroomRole2}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={9}
                  {...register(id, {
                    validate: (value) =>
                      validatePercentage(value, classroomRole2),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h5 className="section-title">Non-Classroom Roles</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole1"
              labelText="Role 1"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={coreData.nonClassroomRoleList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole1PercentOfTime"
              labelText="Role 1 Full-Time Equivalency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!nonClassroomRole1}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={9}
                  {...register(id, {
                    validate: (value) =>
                      validatePercentage(value, nonClassroomRole1),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole2"
              labelText="Role 2"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={coreData.nonClassroomRoleList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole2PercentOfTime"
              labelText="Role 2 Full-Time Equivalency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!nonClassroomRole2}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={9}
                  {...register(id, {
                    validate: (value) =>
                      validatePercentage(value, nonClassroomRole2),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole3"
              labelText="Role 3"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(id)}
                  options={coreData.nonClassroomRoleList}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="NonClassroomRole3PercentOfTime"
              labelText="Role 3 Full-Time Equivalency"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!nonClassroomRole3}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={9}
                  {...register(id, {
                    validate: (value) =>
                      validatePercentage(value, nonClassroomRole3),
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <span
        id="spnAddOrEditStaffErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
